import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider } from "@aws-amplify/ui-react";
import {
	AwsCredentialProvider,
	FaceLivenessDetectorCore,
} from "@aws-amplify/ui-react-liveness";
import "@aws-amplify/ui-react/styles.css";
import { ButtonBack } from "../../../../core/components/CustomButton";
import { TextComponent } from "../../../../core/components/TextComponent";
import CircularProgress from "../../../../core/components/CircularProgress";
import { ModalSimple } from "../../../../core/components/Modal";
import { CheckVerifyBG } from "../../../../core/icons/CheckVerifyBG";
import { useAlert } from "../../../../core/hooks/useAlert";
import { AppDispatch, RootState } from "../../../../core/store/store";
import {
	VerificationLivenessFlow,
	VerificationUploadResult,
} from "../../domain/entities/VerificationUploadResult";
import {
	onCreateAdminLiveFaceSession,
	onCreateVerificationLivenessSession,
	onSaveAdminLiveFaceResult,
	onSaveVerificationLivenessResult,
} from "../store/SettingsThunk";
import {
	onGetProfileConfig,
	onGetUser,
} from "../../../profile/presentation/store/OnboardingThunk";
import { setUpdateGetProfileConfig } from "../../../profile/presentation/store/OnboardingSlice";

type VerificationLivenessLocationState = {
	result?: VerificationUploadResult;
	flow?: VerificationLivenessFlow;
};

const ADMIN_LIVE_FACE_RESULT_RETRY_COUNT = 4;
const ADMIN_LIVE_FACE_RESULT_RETRY_DELAY_MS = 2000;

const wait = (milliseconds: number) =>
	new Promise((resolve) => window.setTimeout(resolve, milliseconds));

export const VerificationLivenessScreen = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch: AppDispatch = useDispatch();
	const { showAlert } = useAlert();
	const isLoading = useSelector((state: RootState) => state.settings.isLoading);
	const accountStatus = useSelector(
		(state: RootState) => state.onboarding.getProfileConfig?.status_account
	);
	const [isSubmittedModalOpen, setIsSubmittedModalOpen] = useState(false);

	const locationState = location.state as VerificationLivenessLocationState | null;
	const flow =
		locationState?.flow ||
		(locationState?.result ? "verification" : undefined) ||
		(accountStatus === "live_face_required" ? "admin-live-face" : "verification");
	const isAdminLiveFaceFlow = flow === "admin-live-face";
	const initialSession = locationState?.result?.livenessSession;
	const [session, setSession] = useState(initialSession);
	const isStartingSessionRef = useRef(false);
	const backPath = isAdminLiveFaceFlow
		? "/settings/statusVerification"
		: "/settings/statusVerification/code";

	useEffect(() => {
		if (session || isStartingSessionRef.current) {
			return;
		}

		isStartingSessionRef.current = true;
		const startAction = isAdminLiveFaceFlow
			? onCreateAdminLiveFaceSession()
			: onCreateVerificationLivenessSession();

		dispatch(startAction).then((action) => {
			if (
				onCreateVerificationLivenessSession.fulfilled.match(action) ||
				onCreateAdminLiveFaceSession.fulfilled.match(action)
			) {
				setSession(action.payload);
				return;
			}

			showAlert({
				type: "error",
				message: isAdminLiveFaceFlow
					? "Could not start the live face check. Please go back and try again."
					: "Could not start the live check. Please go back and try again.",
			});
			isStartingSessionRef.current = false;
		});
	}, [dispatch, isAdminLiveFaceFlow, session, showAlert]);

	const credentialProvider = useMemo<AwsCredentialProvider | undefined>(() => {
		if (!session) {
			return undefined;
		}

		return async () => ({
			accessKeyId: session.accessKeyId,
			secretAccessKey: session.secretAccessKey,
			sessionToken: session.sessionToken,
			expiration: session.expiresAt ? new Date(session.expiresAt) : undefined,
		});
	}, [session]);

	const getSaveMessage = (status: string) => {
		if (isAdminLiveFaceFlow) {
			if (status === "expired") {
				return "Live face check expired. Your account will remain suspended. You can try again.";
			}
			return "We could not confirm a match to your current profile photo. Your account will remain suspended. You can update your main profile photo and try again.";
		}

		switch (status) {
			case "failed":
				return "Live check could not confirm your face. Please try again.";
			case "expired":
				return "Live check expired. Please try again.";
			case "pending_result":
				return "Live check is still being checked. Please wait a moment and try again.";
			default:
				return "Live check could not be saved. Please try again.";
		}
	};

	const handleAnalysisComplete = async () => {
		if (!session?.sessionId) {
			showAlert({
				type: "error",
				message: "Live check session is missing. Please try again.",
			});
			return;
		}

		const saveLiveFaceResult = async () => {
			const action = await dispatch(
				isAdminLiveFaceFlow
					? onSaveAdminLiveFaceResult({ sessionId: session.sessionId })
					: onSaveVerificationLivenessResult({ sessionId: session.sessionId })
			);

			if (
				onSaveVerificationLivenessResult.fulfilled.match(action) ||
				onSaveAdminLiveFaceResult.fulfilled.match(action)
			) {
				return action.payload;
			}

			return null;
		};

		let liveFaceStatus = await saveLiveFaceResult();

		if (isAdminLiveFaceFlow) {
			for (
				let attempt = 0;
				liveFaceStatus === "pending_result" &&
				attempt < ADMIN_LIVE_FACE_RESULT_RETRY_COUNT;
				attempt += 1
			) {
				await wait(ADMIN_LIVE_FACE_RESULT_RETRY_DELAY_MS);
				liveFaceStatus = await saveLiveFaceResult();
			}
		}

		if (liveFaceStatus) {
			if (liveFaceStatus === "passed" && isAdminLiveFaceFlow) {
				dispatch(setUpdateGetProfileConfig("verified"));
				void dispatch(onGetProfileConfig());
				void dispatch(onGetUser());
				navigate("/settings/statusVerification", {
					replace: true,
					state: { adminLiveFaceStatus: "passed" },
				});
				return;
			}

			if (liveFaceStatus === "passed") {
				setIsSubmittedModalOpen(true);
				return;
			}

			if (isAdminLiveFaceFlow) {
				dispatch(setUpdateGetProfileConfig("live_face_required"));
				void dispatch(onGetProfileConfig());
				void dispatch(onGetUser());
				navigate("/settings/statusVerification", {
					replace: true,
					state: {
						adminLiveFaceStatus:
							liveFaceStatus === "expired"
								? "expired"
								: liveFaceStatus === "pending_result"
									? "pending_result"
									: "failed",
					},
				});
				return;
			}

			showAlert({
				type: liveFaceStatus === "pending_result" ? "info" : "error",
				message: getSaveMessage(liveFaceStatus),
			});
			return;
		}

		showAlert({
			type: "error",
			message: isAdminLiveFaceFlow
				? "Could not save your live face check. Please try again."
				: "Could not save your live check. Please try again.",
		});
	};

	const handleError = (error: any) => {
		showAlert({
			type: "error",
			message:
				error?.state === "CAMERA_ACCESS_ERROR"
					? isAdminLiveFaceFlow
						? "Camera access is required for the live face check."
						: "Camera access is required for the live check."
					: isAdminLiveFaceFlow
						? "Live face check could not be completed. Please try again."
						: "Live check could not be completed. Please try again.",
		});
	};

	if (!session || !credentialProvider) {
		return (
			<div className="bg-white p-8">
				<ButtonBack onClick={() => navigate(backPath)}>
					Go Back
				</ButtonBack>
				{isLoading ? (
					<div className="mt-8 flex min-h-[320px] items-center justify-center">
						<CircularProgress />
					</div>
				) : (
					<div className="mt-8">
						<TextComponent variant="h3" className="font-bold">
							{isAdminLiveFaceFlow ? "Live face check" : "Live check"}
						</TextComponent>
						<TextComponent variant="h6" className="mt-3">
							{isAdminLiveFaceFlow
								? "Live face check details are missing. Please go back and try again."
								: "Live check details are missing. Please go back and try again."}
						</TextComponent>
					</div>
				)}
			</div>
		);
	}

	return (
		<>
			<div className="bg-white p-8 max-md:overflow-y-auto">
				<ButtonBack onClick={() => navigate(backPath)}>
					Go Back
				</ButtonBack>
				<div className="mt-8 mb-6">
					<TextComponent variant="h3" className="font-bold">
						{isAdminLiveFaceFlow ? "Live face check" : "Live check"}
					</TextComponent>
					<TextComponent variant="h6" className="mt-3 md:w-3/4">
						{isAdminLiveFaceFlow
							? "Complete the live face check to restore your account."
							: "One extra live check is needed before your verification can continue."}
					</TextComponent>
				</div>
				<div className="live-face-detector-frame rounded-3xl overflow-hidden bg-grayDefault md:mx-auto md:max-w-[640px]">
					{isLoading ? (
						<div className="flex min-h-[360px] items-center justify-center">
							<CircularProgress />
						</div>
					) : (
						<ThemeProvider>
							<FaceLivenessDetectorCore
								sessionId={session.sessionId}
								region={session.region}
								onAnalysisComplete={handleAnalysisComplete}
								onError={handleError}
								onUserCancel={() => navigate(backPath)}
								config={{ credentialProvider }}
							/>
						</ThemeProvider>
					)}
				</div>
			</div>
			<ModalSimple
				title="Information"
				variant="secondary"
				actionExecuted={isSubmittedModalOpen}
				onClose={() => {
					setIsSubmittedModalOpen(false);
					navigate("/settings");
				}}
				icon={<CheckVerifyBG />}
				next
			>
				<TextComponent variant="h5">
					Thanks for submitting your photo! Please allow up to 24 hours for our staff to manually verify your profile.
				</TextComponent>
			</ModalSimple>
		</>
	);
};
